"use client";

import {
  Suspense,
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";

import type { LadderSheetPrice } from "@/domain/sheet-ladder";

/** The next sheet's price for the popup's hint, in öre: `price` with the ladder, `ordinaryPrice` without. */
export type AddedToCartPopupNextSheet = Pick<LadderSheetPrice, "price" | "ordinaryPrice">;

/**
 * What the add-to-cart popup shows (spec #107, #112). Quoted by the
 * configurator from the sheet ladder when the tap is made and again, with the
 * server's sheet count, when the add succeeds — held here, so a remount —
 * whose own cart-sheet read starts back at 0 — cannot make the hint flash a
 * different price.
 */
export interface AddedToCartPopupPayload {
  /** Ladder-eligible sheets in the cart after the add. */
  cartSheetCount: number;
  /**
   * The next sheet of the type just added, in öre: `price` with the ladder,
   * `ordinaryPrice` without. `null` when no price was known at submit time —
   * the popup then confirms the add without a hint.
   */
  nextSheet: AddedToCartPopupNextSheet | null;
}

interface AddedToCartPopupContextValue {
  /**
   * The open add-to-cart popup, or `null`. Layout-level so it survives the
   * configurator remount that the first add's cookie write triggers (Next.js
   * soft-refreshes when a server action sets a cookie, which briefly
   * suspends the page-level Suspense boundary). It stays open until the
   * buyer dismisses it or navigates to another page, so navigating away and
   * back does not re-open it.
   */
  addedToCartPopup: AddedToCartPopupPayload | null;
  /** True while the open popup's add is still on its way to Shopify. */
  isAddingToCart: boolean;
  /**
   * Open the popup the moment "Lägg i varukorg" is tapped, in its "adding"
   * state, with the figures predicted from the cart as the click saw it.
   */
  openAddedToCartPopup: (predicted: AddedToCartPopupPayload) => void;
  /**
   * Settle the popup `openAddedToCartPopup` opened: the server's figures on
   * success, `null` to close it on failure. A no-op once the buyer has
   * dismissed it — a popup closed mid-add is not reopened by the add landing.
   * Adds never overlap (the configurator can't submit while one is in
   * flight), so the open popup is always this add's.
   */
  settleAddedToCartPopup: (confirmed: AddedToCartPopupPayload | null) => void;
  closeAddedToCartPopup: () => void;
}

interface AddedToCartPopupState {
  payload: AddedToCartPopupPayload;
  isAdding: boolean;
}

const AddedToCartPopupContext = createContext<AddedToCartPopupContextValue | null>(null);

export function AddedToCartPopupProvider({ children }: { children: ReactNode }) {
  const [popup, setPopup] = useState<AddedToCartPopupState | null>(null);
  const openAddedToCartPopup = useCallback((predicted: AddedToCartPopupPayload) => {
    setPopup({ payload: predicted, isAdding: true });
  }, []);
  const settleAddedToCartPopup = useCallback((confirmed: AddedToCartPopupPayload | null) => {
    setPopup((current) => {
      if (!current?.isAdding) {
        return current;
      }
      return confirmed ? { payload: confirmed, isAdding: false } : null;
    });
  }, []);
  const closeAddedToCartPopup = useCallback(() => {
    setPopup(null);
  }, []);

  return (
    <AddedToCartPopupContext.Provider
      value={{
        addedToCartPopup: popup?.payload ?? null,
        isAddingToCart: popup?.isAdding ?? false,
        openAddedToCartPopup,
        settleAddedToCartPopup,
        closeAddedToCartPopup,
      }}
    >
      {/*
        Own Suspense boundary: `usePathname()` is dynamic under Cache
        Components, and reading it directly in this root-layout provider
        would make every dynamic route block on it at prerender.
      */}
      <Suspense fallback={null}>
        <CloseOnNavigation onNavigate={closeAddedToCartPopup} />
      </Suspense>
      {children}
    </AddedToCartPopupContext.Provider>
  );
}

/**
 * Leaving the page (browser back is the realistic way; the modal covers the
 * header links) drops the popup rather than just hiding it, so coming back
 * does not re-open a stale one. A layout effect, so the close lands before
 * the new page paints. The remount the first add triggers keeps the pathname,
 * so it does not close the popup.
 */
function CloseOnNavigation({ onNavigate }: { onNavigate: () => void }) {
  const pathname = usePathname();
  const lastPathnameRef = useRef(pathname);
  useLayoutEffect(() => {
    if (lastPathnameRef.current === pathname) return;
    lastPathnameRef.current = pathname;
    onNavigate();
  }, [pathname, onNavigate]);
  return null;
}

export function useAddedToCartPopup(): AddedToCartPopupContextValue {
  const ctx = useContext(AddedToCartPopupContext);
  if (!ctx) {
    return {
      addedToCartPopup: null,
      isAddingToCart: false,
      openAddedToCartPopup: () => {},
      settleAddedToCartPopup: () => {},
      closeAddedToCartPopup: () => {},
    };
  }
  return ctx;
}
